import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ProducerEvent, ProducerEventWithBookings, CreateEventData } from '@/types/events';
import { isActivityPubliclyVisible } from '@/lib/events/publicVisibility';

export function useProducerEvents(producerId: string | undefined) {
  return useQuery({
    queryKey: ['producer-events', producerId],
    queryFn: async (): Promise<ProducerEventWithBookings[]> => {
      if (!producerId) return [];
      
      const { data, error } = await supabase
        .rpc('get_producer_events_with_bookings' as any, {
          p_producer_id: producerId,
          p_limit: 500,
        });
      
      if (error) throw error;
      return (data || []) as unknown as ProducerEventWithBookings[];
    },
    enabled: !!producerId,
  });
}

export function useProducerEvent(eventId: string | undefined) {
  return useQuery({
    queryKey: ['producer-event', eventId],
    queryFn: async (): Promise<ProducerEvent | null> => {
      if (!eventId) return null;
      
      const { data, error } = await supabase
        .from('producer_events')
        .select('*')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      return data as ProducerEvent;
    },
    enabled: !!eventId,
  });
}

export function usePublicProducerEvents(producerId: string | undefined) {
  return useQuery({
    queryKey: ['public-producer-events', producerId],
    queryFn: async (): Promise<ProducerEventWithBookings[]> => {
      if (!producerId) return [];
      
      const { data, error } = await supabase
        .rpc('get_public_events_with_bookings' as any, {
          p_producer_id: producerId,
          p_limit: 500,
        });
      
      if (error) throw error;
      const rows = (data || []) as unknown as ProducerEventWithBookings[];
      const now = new Date();
      return rows.filter((event) =>
        isActivityPubliclyVisible(
          {
            timingMode: (event as any).timing_mode || 'date_time',
            startDate: event.start_date,
            endDate: event.end_date,
          },
          now,
        ),
      );
    },
    enabled: !!producerId,
  });
}

export function usePublicEvent(eventId: string | undefined) {
  return useQuery({
    queryKey: ['public-event', eventId],
    queryFn: async (): Promise<ProducerEventWithBookings | null> => {
      if (!eventId) return null;
      
      const { data, error } = await supabase
        .rpc('get_public_events_with_bookings' as any, {
          p_event_id: eventId,
          p_limit: 1,
        });
      
      if (error) throw error;
      const row = ((data as unknown as ProducerEventWithBookings[] | null)?.[0] ?? null) as ProducerEventWithBookings | null;
      if (import.meta.env.DEV && row && (row as any).booking_mode === undefined) {
        console.warn('[public-event] booking_mode missing from get_public_events_with_bookings RPC');
      }
      return row;
    },
    enabled: !!eventId,
  });
}

/**
 * Error thrown when plan limit is exceeded
 */
export class PlanLimitError extends Error {
  featureKey: string;
  limit: number;
  
  constructor(featureKey: string, limit: number) {
    super(`Du har nått gränsen för din plan (max ${limit} ${featureKey === 'events' ? 'event' : featureKey}).`);
    this.name = 'PlanLimitError';
    this.featureKey = featureKey;
    this.limit = limit;
  }
}

/**
 * Parse limit_reached error from Supabase
 */
function parseLimitError(error: { message?: string; code?: string }): PlanLimitError | null {
  if (error.code === 'P0001' && error.message?.includes('limit_reached')) {
    // Format: "limit_reached:events max 3"
    const match = error.message.match(/limit_reached:(\w+) max (\d+)/);
    if (match) {
      return new PlanLimitError(match[1], parseInt(match[2], 10));
    }
  }
  return null;
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateEventData) => {
      const { data: event, error } = await supabase
        .from('producer_events')
        .insert(data)
        .select()
        .single();
      
      if (error) {
        // Check if this is a limit error
        const limitError = parseLimitError(error);
        if (limitError) throw limitError;
        throw error;
      }
      return event as ProducerEvent;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['producer-events', variables.producer_id] });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<ProducerEvent> & { id: string }) => {
      // Defensive guard: reject if a DOM/SyntheticEvent was accidentally passed
      if (typeof (data as any)?.nativeEvent === 'object' || typeof (data as any)?.bubbles === 'boolean') {
        throw new Error('[useUpdateEvent] Received a SyntheticEvent/DOM node instead of a patch payload. Check onClick wiring.');
      }

      // Save ≠ Publish guardrail: status must never leak into a plain save.
      // Publish/Unpublish handlers set status explicitly via buildEventData(statusOverride).
      // If status somehow ends up here without intentional override, strip it to prevent regression.
      if ('status' in data) {
        if (import.meta.env.DEV) {
          console.warn('[useUpdateEvent] status field present in patch — allowed only for publish/unpublish actions.');
        }
      }

      const { data: event, error } = await supabase
        .from('producer_events')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return event as ProducerEvent;
    },
    onSuccess: (event) => {
      // Producer-portal keys
      queryClient.invalidateQueries({ queryKey: ['producer-events', event.producer_id] });
      queryClient.invalidateQueries({ queryKey: ['producer-event', event.id] });
      // Public surface keys — ensures venue/field changes are visible immediately
      queryClient.invalidateQueries({ queryKey: ['public-event', event.id] });
      queryClient.invalidateQueries({ queryKey: ['public-event-occurrences', event.id] });
      queryClient.invalidateQueries({ queryKey: ['public-producer-events', event.producer_id] });
      queryClient.invalidateQueries({ queryKey: ['public-producer-events-rpc', event.producer_id] });
      queryClient.invalidateQueries({ queryKey: ['event-public-stats', event.id] });
      // Landing & feed keys (predicate match to cover variable params like max)
      queryClient.invalidateQueries({ predicate: q => q.queryKey[0] === 'landing-events' });
      queryClient.invalidateQueries({ queryKey: ['outings'] });
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, producerId }: { id: string; producerId: string }) => {
      const { error } = await supabase
        .from('producer_events')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { id, producerId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['producer-events', variables.producerId] });
    },
  });
}
